export class Category {
    constructor(public id: number,
                public name: string,
                public hasSubCategory: boolean,
                public parentId: number,
                public creativePrice: number,
                public editorialPrice: number) {
    }
}

export class Results {

    constructor(public id: string,
                public filename: string,
                public caption: string,
                public desc: string,
                public model: string,
                public license: string,
                public collection: string,
                public category: string,
                public property: string,
                public author: string,
                public tags: Array<any>,
                public max_size: string,
                public restrictions: string,
                public thumbnail: string,
                public preview: string,
                public sizes: Sizes[],
                public large_thumbnail: string,
                public medium_thumbnail: string,
                public time: string,
                public isHover: boolean,
                public contributor: string,
                public ext: string,
                public usage: string,
                public location: string,
                public audio_channel: string,
                public compressor: string,
                public type: string,
                public iseditorial: boolean,
                public flexGrow: string = "1 !important",
                public error: string,
                public similar: any,
                public series: any,
                public same_model: any,
    ) {
    }
}

export class CartData {
    constructor(public id: number,
                public filename: string,
                public license: string,
                public collection: string,
                public size: string,
                public thumbnail: string,
                public preview: string,
                public price: number,
                public cartCount: number,
                public type: string,
                public caption: string,
                public download_link: string,
                public category: string,
                public searchText: string,
    ) {
    }
}

export class CartStatus {
    constructor(public statusCode: number,
                public data: CartList[]
    ) {
    }
}

export class Plan {
    constructor(public id: number,
                public name: string,
                public sizes: string,
                public collection_id: string,
                public no_of_images: number,
                public no_of_prompt: number,
                public days_validity: number,
                public amount: number,
                public status: number,
                public media_type: string,
                public package_type: string,
                public license_type: string,
                public isActivated?: boolean,
                public data?: any

    ) {
    }
}

/**
 * One priced tool line of a credit pack — a row of the pack's `details`.
 *
 * The money and credit columns arrive as strings because they are MySQL
 * decimals; anything doing arithmetic on them has to coerce first.
 */
export interface CreditPackTool {
    id: number;
    credit_pack_id: number;
    service_code: string;       // chat.turn, edit.remove_bg, image.imagen …
    service_label: string;
    group_key: string;          // the part of service_code before the dot
    /*
     * base_inr, base_credits and markup_pct are deliberately NOT declared.
     *
     * They are what a tool costs US and what we mark it up by, and the pricing
     * endpoint is public. Typing them here is what makes reading them look
     * sanctioned, so the type stops at what the UI actually renders — the sell
     * columns. The endpoint still has to stop sending them (backend: $hidden on
     * the pack-detail model); until it does, nothing in this app can grow a
     * dependency on them by accident.
     */
    sell_inr: string;
    sell_credits: string;       // what the tool costs the customer, in credits
    max_uses_per_month: number | null;   // null = unlimited
}

/**
 * A credit pack template, as auth/plan now returns it. This replaced the old
 * Plan rows on the pricing page: a pack sells a pot of credits that every tool
 * draws from, rather than a fixed image count.
 */
export interface CreditPack {
    id: number;
    pack_name: string;
    pack_type: string;          // image | video
    duration: string;           // monthly | quarterly | half-yearly | yearly
    total_credits: number;
    license_type: string;       // basic | commercial | enterprise
    redownload_days: number | null;
    comp_watermarked: number;
    collection_ids: string;     // "13,222,223" — stock collections on offer
    credit_value_inr: string;   // ₹ per credit, so price = credits × this

    /*
     * Catalogue columns, added on the table after the pricing page was built.
     * auth/plan returns the model itself, so these arrive with every pack.
     *
     * They are optional here because rows written before the columns existed
     * carry the DB defaults rather than anything chosen, and because the
     * pricing endpoints have shipped without them — a template reading
     * `pack.title` must cope with it being absent.
     *
     * `pack` is a SET column: it comes back as a comma separated string and may
     * legitimately hold BOTH values, so it is not safe to compare with ===.
     * Split it, or use pack_list where the endpoint provides it.
     */
    type?: 'STANDARD' | 'ENHANCED';
    media?: 'IMAGE' | 'VIDEO' | 'BOTH';
    pack?: string;              // "LARGE_PACK", "SMALL_PACK", or both
    pack_list?: string[];       // the same value split — admin endpoints only
    title?: string;
    sub_title?: string;
    description?: string;

    status: number;
    ip_pack_status: number;     // 0 hides the pack from the IndiaPicture site
    details: CreditPackTool[];
}

/*
 * Which section of the pricing page a pack belongs to.
 *
 * auth/plan returns every pack on sale in one list, and the page shows them in
 * two places that must not overlap: the subscription rail (and the AI pricing
 * dialog) draw LARGE_PACK, the one-time section draws SMALL_PACK. Without the
 * split, fourteen one-time packs at up to ₹2,00,000 land in the middle of the
 * monthly tiers.
 *
 * Here rather than in either page because three components need the same answer,
 * and a rule that lives in one of them is a rule the others will disagree with.
 *
 * `pack` is a SET column — "LARGE_PACK", "SMALL_PACK", or both — so these test
 * membership rather than comparing the whole string.
 */
export function isLargePack(pack: CreditPack): boolean {
    /*
     * MISSING COUNTS AS LARGE. The column was added long after the subscription
     * packs existed and defaults to LARGE_PACK, and a response cached from before
     * it shipped carries no `pack` at all. Treating that as "not large" would
     * empty the subscription rail — the main thing on the page — on the strength
     * of a field that simply was not there yet.
     */
    return !(pack.pack || '').trim() || packSegments(pack).indexOf('LARGE_PACK') !== -1;
}

/* No such fallback here: a pack is one-time only if it says so. */
export function isSmallPack(pack: CreditPack): boolean {
    return packSegments(pack).indexOf('SMALL_PACK') !== -1;
}

function packSegments(pack: CreditPack): string[] {
    return (pack.pack || '').split(',').map(v => v.trim().toUpperCase()).filter(v => !!v);
}

export class Order {
    created_at: string;
    plan_id: number;
    tax_amount: number;
    order_id: any;
    total_images: any;
    total_remaining: any;
    total_prompt: any;
    prompt_remaining: any;
    validity: string;
    total_downloaded:number;
    constructor(public id: number,
                public date: string,
                public status: string,
                public amount: string,
                public taxAmount: string,
                public collection: string,
                public sizes: string,
                public plan_detail: string
    ) {
    }
}

export class Sizes {
    constructor(public name: string,
                public pixels: string,
                public inches: string,
                public dpi: string,
                public price: number,
                public price_usd: number,
                public ext: string,
                public sizeMb: string,
                public video_downloadable: boolean,
                public subscription: number,
                public credit: number,
                public download_link: string,
                public is_downloaded: boolean) {
    }
}

export class User {
    customer_gst_no: string;
    pan_card: string;
    constructor(public id: number,
                public customers_id: string,
                public customers_firstname: string,
                public customers_lastname: string,
                public customers_email_address: string,
                public customers_city: string,
                public customers_state: string,
                public customers_telephone: string,
                public customers_company: string,
                public customers_address_line1: string,
                public customers_address_line2: string,
                public customers_comp_status: string,
                public customers_postcode: string,
                public customers_country: string,
                public customers_password: string,
                public customers_password1: string,
                public company_type: string,
                public job_description: string,
                public customers_fax: string,
                public gst_no: string,
                public pan_no: string,
                public plan_id: any,
                public license_type:any,
                public token: string) {
    }
}

export class WishList {
    statusCode: number;
    constructor(public id: number,
                public customer_id: string,
                public name: string,
                public no_of_images: string,
                public no_of_sharing: string,
                public created_at: string,
                public imagesArray: Results[]) {
    }
}

export class CartList {
    constructor(public imagesArray: CartData[],
                public total_price: string,
                public total_item: number) {
    }

}

export class Subscription {
    constructor(public name: string,
                public subscription: number,
                public credit: number,
                public download_link: string,
                public is_downloaded: boolean,
                public price_high: number) {
    }
}

export class Auth {
    constructor(public name: string,
                public email: string,
                public password: string,
                public phone: string) {
    }


}

export class ContributorData {
    constructor(public username: string,
                public firstname: string,
                public lastname: string,
                public photograph: string,
                public interest: string,
                public code: string,
                public bio: string,
    ) {
    }
}

export class Contact {
    id?: number = 0;
    public name: string;
    public email: string;
    public contact: string;
    public requirement: string;
    public subject: string;
    public page_url: string;

    public constructor() {
    }
}

export declare type LinkDefinition = {
    charset?: string;
    crossorigin?: string;
    href?: string;
    hreflang?: string;
    media?: string;
    rel?: string;
    rev?: string;
    sizes?: string;
    target?: string;
    type?: string;
} & {
    [prop: string]: string;
};


export class Data {
    constructor(public cartList: CartData[],
                public totalPrice: number,
                public totalCartCount: number) {
    }
};

export class UsageSubscription {
    constructor(public comp_status: number,
                public usage: any,
                public price_high: number,
                public price_low: number,
                public name: string,
            public subscription: number,
                public credit: number,
                public download_link: string,
                public is_downloaded: boolean) {

    }

}

export class UsageType {
    constructor(public name: string,
                public price: number,
                public size: Sizes[],
                public subscription: number) {
    }
}

export class Images {
    constructor(public total: number,
                public total_images:number,
                public pages: number,
                public results: Results[]) {
    }
}